import { MIN_Y, MAX_HEIGHT } from "../constants.js";
import { mulberry32 } from "../noise.js";
import { visit, writeHeight } from "./mask.js";

function clampHeight(height) {
    return Math.max(MIN_Y, Math.min(MAX_HEIGHT, Math.round(height)));
}

function coneAt(dist, radius, height) {
    if (radius <= 0) return 0;
    const t = 1 - dist / radius;
    if (t <= 0) return 0;
    // Eased cone: the foot flattens into the plain, the summit stays a peak.
    return height * t ** 1.45;
}

function buildCones(seed) {
    const random = mulberry32(seed >>> 0);
    const main = {
        u: (random() * 2 - 1) * 0.07,
        v: (random() * 2 - 1) * 0.07,
        height: 1,
        radius: 0.72 + random() * 0.1,
    };
    const cones = [main];
    const subCount = 3 + (random() < 0.45 ? 1 : 0);
    const base = random() * Math.PI * 2;

    for (let s = 0; s < subCount; s++) {
        const angle = base + (s * Math.PI * 2) / subCount + (random() - 0.5) * 0.4;
        const dist = 0.42 + random() * 0.2;
        const u = main.u + Math.cos(angle) * dist;
        const v = main.v + Math.sin(angle) * dist;
        const shoulder = coneAt(dist, main.radius, main.height);
        const height = Math.min(0.76, shoulder + 0.36 + random() * 0.1);
        const radius = main.radius * height * (0.82 + random() * 0.16);
        cones.push({ u, v, height, radius });
    }

    return cones;
}

export default function applyMountains(heights, resolution, layoutSize, feature, noise) {
    const strength = feature.strength ?? 1;
    const lift = feature.height * strength;
    if (lift === 0) return;

    const cones = buildCones(feature.seed ?? 1);

    visit(feature, resolution, layoutSize, (idx, u, v) => {
        const warp = noise.noise2(u * 1.35 + 2.4, v * 1.35 + 6.1) * 0.045;

        let shape = 0;
        for (let n = 0; n < cones.length; n++) {
            const cone = cones[n];
            const dist = Math.hypot(u - cone.u, v - cone.v) + warp;
            const h = coneAt(dist, cone.radius, cone.height);
            if (h > shape) shape = h;
        }
        if (shape <= 0) return;

        const broad = noise.fbm(u * 2.6 + 1.4, v * 2.6 + 4.8, 3);
        const ribs = 1 - Math.abs(noise.fbm(u * 4.8 + 8.2, v * 4.8 + 1.7, 4));
        const grit = noise.noise2(u * 10.5 + 3.3, v * 10.5 + 7.1);
        const rugged = (broad * 0.1 + (ribs - 0.5) * 0.16 + grit * 0.045) * shape;

        writeHeight(heights, idx, clampHeight(heights[idx] + (shape + rugged) * lift));
    });
}
